/**
 * Queries to the ESP Component Registry's API: components that match a search,
 * and the versions of one component. The API answers with CORS headers for the
 * origin that asks, so a page can query it too.
 */

export const REGISTRY_URL = "https://components.espressif.com";

export interface RegistryHit {
    /** `namespace/name`. */
    name: string;
    latest: string;
    description: string;
    /** The chips the latest version supports; empty when it supports all. */
    targets: string[];
}

export interface RegistryVersion {
    version: string;
    targets: string[];
    yanked: boolean;
}

export type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

export function registryPage(name: string): string {
    return `${REGISTRY_URL}/components/${name}`;
}

interface ApiComponent {
    namespace: string;
    name: string;
    latest_version?: { version?: string; description?: string; targets?: string[] };
}

export class Registry {
    readonly #fetch: Fetch;
    readonly #searches = new Map<string, Promise<RegistryHit[]>>();
    readonly #versions = new Map<string, Promise<RegistryVersion[]>>();

    constructor(fetch: Fetch = (url, init) => globalThis.fetch(url, init)) {
        this.#fetch = fetch;
    }

    /** Components whose name or description matches `query`, as the registry ranks them. */
    search(query: string): Promise<RegistryHit[]> {
        const key = query.trim().toLowerCase();
        let found = this.#searches.get(key);
        if (found === undefined) {
            const url = `${REGISTRY_URL}/api/components?per_page=20${key === "" ? "&sort_by=featured" : `&q=${encodeURIComponent(key)}`}`;
            found = this.#json<ApiComponent[]>(url).then((components) =>
                components.map((c) => ({
                    name: `${c.namespace}/${c.name}`.toLowerCase(),
                    latest: c.latest_version?.version ?? "",
                    description: c.latest_version?.description ?? "",
                    targets: c.latest_version?.targets ?? [],
                })),
            );
            found.catch(() => this.#searches.delete(key));
            this.#searches.set(key, found);
        }
        return found;
    }

    /** The versions of the component `namespace/name`, newest first; none when the registry does not have it. */
    versions(name: string): Promise<RegistryVersion[]> {
        const key = name.toLowerCase();
        let found = this.#versions.get(key);
        if (found === undefined) {
            found = this.#json<{ versions?: { version: string; targets?: string[]; yanked_at?: string | null }[] }>(
                `${REGISTRY_URL}/api/components/${key}`,
                true,
            ).then((body) =>
                (body?.versions ?? []).map((v) => ({
                    version: v.version,
                    targets: v.targets ?? [],
                    yanked: v.yanked_at != null,
                })),
            );
            found.catch(() => this.#versions.delete(key));
            this.#versions.set(key, found);
        }
        return found;
    }

    async #json<T>(url: string, missingIsEmpty = false): Promise<T> {
        const response = await this.#fetch(url);
        if (missingIsEmpty && response.status === 404) return undefined as T;
        if (!response.ok) throw new Error(`The ESP Component Registry answered ${response.status}.`);
        return (await response.json()) as T;
    }
}
